import React, { useRef, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import VideoPlayer from "./VideoPlayer";
import RangeSlider from "./RangeSlider";
import {
  fetchDetectionList,
  fetchDetectionResultById,
  uploadUpdatedData,
  fetchVideoList,
  getVideoPlaybackUrl,
} from "../api";
import "../styles/VideoPlayback.css";
import Cookies from "js-cookie";

const VideoPlayback = () => {
  const location = useLocation(); // Access the location object
  // Parse the query parameters
  const queryParams = new URLSearchParams(location.search);
  const state = queryParams.get("state"); // Get the serialized state from the query parameter
  const video = state ? JSON.parse(decodeURIComponent(state)).video : null; // Extract the video object
  const { video_id: videoId } = video;
  const [videoUrl, setVideoUrl] = useState(null); // State to store the video URL
  const [currentTime, setCurrentTime] = useState(0);
  const [rectangles, setRectangles] = useState([]);
  const [scores, setScores] = useState([]);
  const [timeRange, setTimeRange] = useState({ start: 0, end: 0 });
  const [duration, setDuration] = useState(0);
  const canvasRef = useRef(null);
  const videoRef = useRef(null);
  const [isEditing, setIsEditing] = useState(true); // State to track editing mode in VideoPlayback
  const [isEditingEnabled, setIsEditingEnabled] = useState(false); // State to track editing mode in VideoPlayer
  const [isLabeled, setIsLabeled] = useState(false);
  const [newRectangle, setNewRectangle] = useState(null);
  const [detectionId, setDetectionId] = useState(null);
  const [nextVideo, setNextVideo] = useState(null);
  const [disableSlider, setDisableSlider] = useState(true);
  const [showCompletePopup, setShowCompletePopup] = useState(false); // State to control popup visibility
  const [hasCompleted, setHasCompleted] = useState(false); // State to control popup visibility
  const [showUploadPopup, setShowUploadPopup] = useState(false); // State to control upload confirmation popup
  const [detectionList, setDetectionList] = useState([]);
  const [userRole, setUserRole] = useState("");
  const [selectedDetectionId, setSelectedDetectionId] = useState("");
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState("");
  const playbackRefreshTimerRef = useRef(null);
  const playbackRequestSequenceRef = useRef(0);
  const playbackErrorRetryCountRef = useRef(0);
  const isMountedRef = useRef(true);
  const PLAYBACK_REFRESH_BUFFER_MS = 30_000;
  const MAX_PLAYBACK_ERROR_RETRIES = 1;

  const clearPlaybackRefreshTimer = () => {
    if (playbackRefreshTimerRef.current) {
      clearTimeout(playbackRefreshTimerRef.current);
      playbackRefreshTimerRef.current = null;
    }
  };

  const applyVideoSource = (nextUrl, preserveState = true) => {
    const videoElement = videoRef.current;
    if (!videoElement || !preserveState || !videoElement.src) {
      setVideoUrl(nextUrl);
      return;
    }

    const snapshot = {
      currentTime: Number.isFinite(videoElement.currentTime)
        ? videoElement.currentTime
        : 0,
      playbackRate: Number.isFinite(videoElement.playbackRate)
        ? videoElement.playbackRate
        : 1,
      shouldResume: !videoElement.paused && !videoElement.ended,
    };

    const restorePlaybackState = () => {
      if (!videoRef.current) {
        return;
      }
      if (Number.isFinite(snapshot.currentTime) && snapshot.currentTime >= 0) {
        videoRef.current.currentTime = snapshot.currentTime;
      }
      videoRef.current.playbackRate = snapshot.playbackRate;
      if (snapshot.shouldResume) {
        videoRef.current.play().catch(() => {
          return null;
        });
      }
    };

    videoElement.addEventListener("loadedmetadata", restorePlaybackState, {
      once: true,
    });
    setVideoUrl(nextUrl);
  };

  const requestPlaybackUrl = async ({ preserveState = true, showLoading = false } = {}) => {
    const requestId = ++playbackRequestSequenceRef.current;
    if (showLoading) {
      setVideoLoading(true);
    }

    try {
      const playback = await getVideoPlaybackUrl(videoId);
      if (!isMountedRef.current || requestId !== playbackRequestSequenceRef.current) {
        return;
      }
      setVideoError("");
      playbackErrorRetryCountRef.current = 0;
      applyVideoSource(playback.url, preserveState);

      clearPlaybackRefreshTimer();
      const expiresAtMs = Date.parse(playback.expires_at || "");
      if (Number.isFinite(expiresAtMs)) {
        const refreshDelayMs = Math.max(0, expiresAtMs - Date.now() - PLAYBACK_REFRESH_BUFFER_MS);
        playbackRefreshTimerRef.current = setTimeout(() => {
          requestPlaybackUrl({ preserveState: true, showLoading: false });
        }, refreshDelayMs);
      }
    } catch (error) {
      if (!isMountedRef.current || requestId !== playbackRequestSequenceRef.current) {
        return;
      }
      const statusCode = error?.response?.status;
      if (statusCode === 401 || statusCode === 403) {
        setVideoError("You are not authorized to view this video.");
      } else if (statusCode === 404) {
        setVideoError("Video not found.");
      } else {
        setVideoError("Failed to load playback URL.");
      }
    } finally {
      if (isMountedRef.current && requestId === playbackRequestSequenceRef.current) {
        setVideoLoading(false);
      }
    }
  };

  const handlePlaybackError = () => {
    if (playbackErrorRetryCountRef.current >= MAX_PLAYBACK_ERROR_RETRIES) {
      setVideoError("Playback failed. Please retry.");
      return;
    }
    playbackErrorRetryCountRef.current += 1;
    requestPlaybackUrl({ preserveState: true, showLoading: false });
  };

  const handleVideoClick = () => {
    const url = `/video-playback?videoId=${nextVideo.video_id}`;
    const state = JSON.stringify({ video: nextVideo }); // Serialize the state to pass it as a query parameter
    window.open(`${url}&state=${encodeURIComponent(state)}`, "_blank"); // Open in a new tab
  };

  const loadVideos = async () => {
    try {
      const videoData = await fetchVideoList(); // Fetch video list from the API
      // Find the index of the video with the given video_id
      const nonLabeledVideosIndex = videoData.non_labeled_videos.findIndex(
        (video) => video.video_id === videoId
      );
      const labeledVideoIndex = videoData.labeled_videos.findIndex(
        (video) => video.video_id === videoId
      );
      if (
        nonLabeledVideosIndex !== -1 &&
        nonLabeledVideosIndex < videoData.non_labeled_videos.length - 1
      ) {
        setNextVideo(videoData.non_labeled_videos[nonLabeledVideosIndex + 1]);
      }

      if (
        labeledVideoIndex !== -1 &&
        labeledVideoIndex < videoData.labeled_videos.length - 1
      ) {
        setNextVideo(videoData.labeled_videos[labeledVideoIndex + 1]);
      }

      return null;
    } catch (error) {
      console.error("Error loading videos:", error);
    }
  };

  const loadDetectionList = async () => {
    try {
      const data = await fetchDetectionList(video.video_id);
      const firstAIDetection = data?.detections?.find((d) =>
        d?.detection_id?.includes("AI")
      );
      setDetectionList(data?.detections || []);
      setDetectionId(firstAIDetection?.detection_id); // Set the detection ID
      setSelectedDetectionId(firstAIDetection?.detection_id || "");
    } catch (error) {
      console.error("Error fetching detection list:", error);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    const userRole = Cookies.get("platform_role"); // adjust key as needed
    setUserRole(userRole);
    if (userRole === "admin" || userRole === "itdog") {
      setDisableSlider(false);
      setIsLabeled(true);
      setIsEditingEnabled(true); // Enable editing when the video reaches the end
      setIsEditing(false); // Enable editing mode
    }
    if (video) {
      requestPlaybackUrl({ preserveState: false, showLoading: true });
      loadDetectionList();
      loadVideos(); // Load videos if already logged in
    }

    return () => {
      isMountedRef.current = false;
      playbackRequestSequenceRef.current += 1;
      clearPlaybackRefreshTimer();
    };
  }, [videoId]);

  const closeCompletePopup = () => {
    setShowCompletePopup(false); // Close the popup
  };

  const loadDetectionResults = async () => {
    try {
      const { predict } = await fetchDetectionResultById(
        video.video_id,
        detectionId
      );
      const rectangles = predict.map(([frame, x1, y1, x2, y2]) => ({
        frame,
        x1,
        y1,
        x2,
        y2,
      }));
      const scores = predict.map(([frame, x1, y1, x2, y2, score]) => ({
        frame,
      }));
      setRectangles(rectangles);

      setScores(scores);
    } catch (error) {
      console.error("Error fetching detection results:", error);
    }
  };

  const handleDownloadDetection = async () => {
    if (!selectedDetectionId) {
      return;
    }

    try {
      const detectionResult = await fetchDetectionResultById(
        video.video_id,
        selectedDetectionId
      );
      const detectionData = JSON.stringify(detectionResult, null, 2);
      const downloadUrl = URL.createObjectURL(
        new Blob([detectionData], { type: "application/json" })
      );
      const link = document.createElement("a");
      const safeVideoName = (video.video_name || video.video_id).replace(
        /[^a-z0-9-_]+/gi,
        "_"
      );
      const safeDetectionId = selectedDetectionId.replace(
        /[^a-z0-9-_]+/gi,
        "_"
      );

      link.href = downloadUrl;
      link.download = `${safeVideoName}_${safeDetectionId}.json`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading detection result:", error);
      alert("Failed to download detection result. Please try again.");
    }
  };

  useEffect(() => {
    if (detectionId) {
      loadDetectionResults();
    }
  }, [detectionId]);

  useEffect(() => {
    if (
      videoRef?.current?.currentTime &&
      videoRef?.current?.currentTime >= videoRef.current.duration &&
      !hasCompleted
    ) {
      setIsEditingEnabled(true); // Enable editing when the video reaches the end
      setIsLabeled(true);
      setDisableSlider(false);
      setShowCompletePopup(true); // Show the popup
      setHasCompleted(true); // Set hasCompleted to true
    }
  }, [currentTime, videoRef?.current?.currentTime]);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (videoElement) {
      const handleLoadedMetadata = () => {
        setDuration(videoElement.duration);
        setTimeRange((prevRange) => ({
          ...prevRange,
          end: videoElement.duration,
        }));
      };

      videoElement.addEventListener("loadedmetadata", handleLoadedMetadata);

      return () => {
        videoElement.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );
      };
    }
  }, [video]);

  const handleTimeUpdate = (event) => {
    setCurrentTime(event.target.currentTime);
  };

  const handleTimeChange = (time) => {
    setCurrentTime(time);
    if (videoRef.current) {
      videoRef.current.currentTime = time;
    }
  };

  const handleUploadUpdatedData = async () => {
    try {
      const combinedData = rectangles.map((rect, index) => {
        const score = scores.find((s) => s.frame === rect.frame)?.score || 0;
        return [rect.frame, rect.x1, rect.y1, rect.x2, rect.y2, score];
      });

      await uploadUpdatedData(videoId, combinedData);
    } catch (error) {
      console.error("Error uploading updated data:", error);
    }
  };

  const updateRectangle = (updatedRectangles) => {
    setRectangles(updatedRectangles);
    console.log("Updated rectangles in App component:", updatedRectangles);
  };

  const updateScore = (updatedScores) => {
    setScores(updatedScores);
    console.log("Updated scores in App component:", updatedScores);
  };

  const handleEyeContact = () => {
    if (videoRef.current) {
      const currentFrame = Math.floor(videoRef.current.currentTime * video.fps);
      const nextFrame = Math.floor(
        (videoRef.current.currentTime + 0.5) * video.fps
      );

      // Save the new rectangle into the rectangles data
      if (newRectangle) {
        const updatedRectangles = rectangles.map((rect) => {
          if (rect.frame >= currentFrame && rect.frame < nextFrame) {
            return { ...newRectangle, frame: rect.frame };
          }
          return rect;
        });

        for (let frame = currentFrame; frame < nextFrame; frame++) {
          if (!updatedRectangles.some((rect) => rect.frame === frame)) {
            updatedRectangles.push({ ...newRectangle, frame });
          }
        }

        updateRectangle(updatedRectangles);
      }

      const updatedScores = scores.map((score) =>
        score.frame >= currentFrame && score.frame < nextFrame
          ? { ...score, score: 1 }
          : score
      );

      for (let frame = currentFrame; frame < nextFrame; frame++) {
        if (!updatedScores.some((score) => score.frame === frame)) {
          updatedScores.push({ score: 1, frame });
        }
      }

      updateScore(updatedScores);
      // Calculate target frame after 0.5 seconds and seek to exact frame boundary
      const targetTime = videoRef.current.currentTime + 0.5;
      const targetFrame = Math.round(targetTime * video.fps);
      videoRef.current.currentTime = targetFrame / video.fps;
    }
  };

  const handleNoEyeContact = () => {
    if (videoRef.current) {
      const currentFrame = Math.floor(videoRef.current.currentTime * video.fps);
      const nextFrame = Math.floor(
        (videoRef.current.currentTime + 0.5) * video.fps
      );

      if (newRectangle) {
        const updatedRectangles = rectangles.map((rect) => {
          if (rect.frame >= currentFrame && rect.frame < nextFrame) {
            return { ...newRectangle, frame: rect.frame };
          }
          return rect;
        });

        for (let frame = currentFrame; frame < nextFrame; frame++) {
          if (!updatedRectangles.some((rect) => rect.frame === frame)) {
            updatedRectangles.push({ ...newRectangle, frame });
          }
        }

        updateRectangle(updatedRectangles);
      }
      const updatedScores = scores.map((score) =>
        score.frame >= currentFrame && score.frame < nextFrame
          ? { ...score, score: 0 }
          : score
      );

      for (let frame = currentFrame; frame < nextFrame; frame++) {
        if (!updatedScores.some((score) => score.frame === frame)) {
          updatedScores.push({ score: 0, frame });
        }
      }

      updateScore(updatedScores);
      // Calculate target frame after 0.5 seconds and seek to exact frame boundary
      const targetTime = videoRef.current.currentTime + 0.5;
      const targetFrame = Math.round(targetTime * video.fps);
      videoRef.current.currentTime = targetFrame / video.fps;
    }
  };

  const handleBack = () => {
    if (videoRef.current) {
      // Calculate target frame after going back 0.5 seconds and seek to exact frame boundary
      const targetTime = Math.max(0, videoRef.current.currentTime - 0.5);
      const targetFrame = Math.round(targetTime * video.fps);
      videoRef.current.currentTime = targetFrame / video.fps;
    }
  };

  const handleStartChange = (start) => {
    setTimeRange((prevRange) => ({ ...prevRange, start }));
    if (videoRef.current && start > currentTime) {
      videoRef.current.currentTime = start;
      setCurrentTime(start);
    }
  };

  const handleEndChange = (end) => {
    setTimeRange((prevRange) => ({ ...prevRange, end }));
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Enter") {
        handleEyeContact(); // Trigger the function when Enter is pressed
      }
      if (event.key === "Shift") {
        handleNoEyeContact(); // Trigger the function when Escape is pressed
      }
      if (event.key === "ArrowLeft") {
        handleBack(); // Trigger the function when ArrowLeft is pressed
      }
    };

    // Add the event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleEyeContact]); // Add handleEyeContact as a dependency

  const handleUploadClick = () => {
    setShowUploadPopup(true); // Show the confirmation popup
  };

  const confirmUpload = async () => {
    try {
      const combinedData = rectangles.map((rect, index) => {
        const score = scores.find((s) => s.frame === rect.frame)?.score || 0;
        return [rect.frame, rect.x1, rect.y1, rect.x2, rect.y2, score];
      });

      await uploadUpdatedData(videoId, combinedData);
      setShowUploadPopup(false); // Close the popup after successful upload
      alert("Data uploaded successfully!");
    } catch (error) {
      console.error("Error uploading updated data:", error);
      alert("Failed to upload data. Please try again.");
    }
  };

  const cancelUpload = () => {
    setShowUploadPopup(false); // Close the popup without uploading
  };

  return (
    <div className="video-playback">
      {video ? (
        <>
          <h1>{video.video_name}</h1>
          <VideoPlayer
            src={videoUrl} // Pass the video URL
            onTimeUpdate={handleTimeUpdate}
            onPlaybackError={handlePlaybackError}
            canvasRef={canvasRef}
            videoRef={videoRef}
            rectangles={rectangles}
            currentTime={currentTime}
            isEditingEnabled={isEditingEnabled}
            timeRange={timeRange}
            scores={scores}
            fps={video.fps}
            frameCount={video.frame_count}
            duration={video.duration}
            width={video.width}
            height={video.height}
            newRectangle={newRectangle} // Pass newRectangle
            setNewRectangle={setNewRectangle} // Pass setNewRectangle
            isEditing={isEditing} // Pass isEditing state
            setIsEditing={setIsEditing} // Pass setIsEditing function
          />
          {videoLoading && <p>Loading video...</p>}
          {videoError && (
            <div>
              <p>{videoError}</p>
              <button onClick={() => requestPlaybackUrl({ preserveState: true, showLoading: true })}>
                Retry
              </button>
            </div>
          )}
          {duration > 0 && !disableSlider && (
            <RangeSlider
              start={timeRange.start}
              end={timeRange.end}
              duration={duration}
              currentTime={currentTime}
              onTimeChange={handleTimeChange}
              onEndChange={handleEndChange}
              onStartChange={handleStartChange}
            />
          )}
          <div className="button-group">
            <button onClick={handleEyeContact}>Eye Contact (Enter)</button>
            <button onClick={handleNoEyeContact}>No Eye Contact (Shift)</button>
            <button onClick={handleBack}>Back (left arrow)</button>
          </div>
          <div className="button-group">
            <button onClick={handleUploadClick} disabled={!isLabeled}>
              Upload Data
            </button>
            <button onClick={handleVideoClick} disabled={!isLabeled}>
              Next
            </button>
          </div>
        </>
      ) : (
        <p>Loading video data...</p>
      )}
      {/* Popup */}
      {showCompletePopup && (
        <div className="popup">
          <div className="popup-content">
            <h2>Labeling Complete</h2>
            <p>You have completed labeling this video.</p>
            <button onClick={closeCompletePopup}>Close</button>
          </div>
        </div>
      )}

      {/* Upload Confirmation Popup */}
      {showUploadPopup && (
        <div className="popup">
          <div className="popup-content">
            <h2>Confirm Upload</h2>
            <p>Are you sure you want to upload the labeled data?</p>
            <div className="popup-buttons">
              <button onClick={confirmUpload}>Yes, Upload</button>
              <button onClick={cancelUpload}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {(userRole === "admin" || userRole === "itdog") &&
        detectionList.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="detection-select" style={{ marginRight: 8 }}>
              Detection:
            </label>
            <select
              id="detection-select"
              value={selectedDetectionId}
              onChange={(e) => {
                setSelectedDetectionId(e.target.value);
                setDetectionId(e.target.value);
              }}
            >
              {detectionList.map((d) => (
                <option key={d.detection_id} value={d.detection_id}>
                  {d.detection_id}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleDownloadDetection}
              disabled={!selectedDetectionId}
              style={{ marginLeft: 8 }}
            >
              Download Detection Result
            </button>
          </div>
        )}
    </div>
  );
};

export default VideoPlayback;
